import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, ChevronRight, ChevronsUpDown } from 'lucide-react';
import { MOVABLE_DOCUMENT_CATEGORIES, adverseKindOfRootFolder, type MovableDocumentCategory } from '@hector/shared-types';
import { cn } from '@/lib/utils';
import { FolderGlyph } from '@/components/icons/FolderGlyph';
import { SELECTION_SURFACE } from '@/components/folder-browser/selectionSurface';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { FolderNode } from '@/services/folderHierarchyService';
import type { Case } from '@/types/api';
import { DOCUMENT_CATEGORIES } from '@/data/documentCategories';
import { getCategoryTabLabel } from '@/lib/droit-public';
import { groupRootFoldersByPartie, kindShortLabel } from '@/lib/partieAdverseFolders';
import {
  toDestinationTree,
  samePath,
  isWithin,
  rootIsSelectable,
  type DestinationFolderNode,
  type DestinationValue,
} from './destinationModel';

/**
 * Le choix d'une DESTINATION de pièce : dossier, onglet, sous-dossier.
 *
 * ─── Pourquoi ce composant existe ─────────────────────────────────────────
 * Quatre écrans posaient la même question de quatre façons. « Emplacement »
 * désignait l'onglet dans l'un et le sous-dossier dans l'autre ; « Sous-dossier »
 * désignait un vrai dossier partout sauf dans un écran où c'était une
 * sous-catégorie ; l'arborescence était rendue de cinq manières (deux arbres, trois
 * aplatissements, dont un à l'indentation inopérante) ; et seuls deux écrans sur
 * quatre laissaient changer de dossier.
 *
 * Le rendu reprend celui de « Déplacer vers » — pastilles d'onglet et arbre
 * repliable — en comblant ses deux manques : le dossier devient choisissable, et
 * la recherche passe par le combobox canonique du dépôt (`Popover` + `Command`)
 * plutôt que par un panneau maison à gestion de clic extérieur.
 *
 * ─── Pur par construction ─────────────────────────────────────────────────
 * Aucun fetch, aucun store, aucun routeur : les données arrivent en props et le
 * connecteur `ConnectedDestinationPicker` s'occupe des hooks. Même partage des
 * rôles que `DecisionView` / `DecisionPreviewPanel` — c'est ce qui rend ce
 * composant testable en story sans mocker le moindre module.
 */
export interface DestinationPickerCase {
  id: string;
  title?: string | null;
  client_name?: string | null;
}

export interface DestinationPickerProps {
  value: DestinationValue;
  /** Dossiers proposés au choix. */
  cases: readonly DestinationPickerCase[];
  /**
   * Dossier sélectionné — libellés d'onglet adaptés au droit public, et nom
   * affiché quand le dossier est verrouillé (la liste n'est alors pas chargée).
   */
  selectedCaseRecord?: Pick<Case, 'procedure_type' | 'title' | 'client_name'> | null;
  /**
   * Arborescence de `useFolderTree` pour le couple (dossier, onglet) courant.
   *
   * Le type COMPLET est exigé, et non une forme allégée : `groupRootFoldersByPartie`
   * lit `path`, `documentType` et `subcategory` pour reconnaître les paires
   * « Partie adverse » — un nœud amputé le fait planter au rendu.
   */
  folderTree: readonly FolderNode[];
  isFolderTreeLoading?: boolean;
  onCaseChange: (caseId: string) => void;
  onCategoryChange: (category: MovableDocumentCategory) => void;
  onFolderPathChange: (folderPath: string[]) => void;
  /** Onglets proposés. Défaut : le catalogue partagé, miroir de la portée de l'agent. */
  categories?: readonly MovableDocumentCategory[];
  /**
   * Destination interdite, et POURQUOI — la raison est affichée sur la ligne.
   * Sert à « Déplacer vers » (emplacement actuel, dossier à l'intérieur de la
   * sélection déplacée) sans que ce composant connaisse ces règles.
   *
   * La sélection COURANTE est passée en second argument plutôt que retenue par
   * l'appelant : la règle de « Déplacer vers » dépend de l'onglet choisi, et un
   * appelant ne l'apprend qu'au rendu suivant (`onSelectionChange` est un effet)
   * — il griserait donc une ligne d'après l'onglet précédent.
   */
  isPathDisabled?: (path: string[], value: DestinationValue) => string | null;
  /**
   * Bandeau libre sous les pastilles (ex. « les éléments changeront de section »).
   * Sous forme de fonction pour la même raison que `isPathDisabled`.
   */
  notice?: React.ReactNode | ((value: DestinationValue) => React.ReactNode);
  /**
   * Affiche le dossier sans permettre d'en changer.
   *
   * WHY cette capacité plutôt que masquer le bloc : « Déplacer vers » ne peut pas
   * changer de dossier — l'API de déplacement (`PATCH /documents/:id`) n'accepte
   * pas de `case_id`, une pièce ne franchit pas la frontière d'un dossier. Mais
   * l'avocat doit quand même LIRE où il range : masquer la ligne le laisserait
   * deviner, et offrir un choix sans effet serait pire — un geste qui paraît
   * réussir sans rien déplacer.
   */
  caseLocked?: boolean;
  /**
   * Chemin à déplier à l'ouverture (l'emplacement d'origine, pour « Déplacer
   * vers »). Sans lui, un dossier rangé à trois niveaux oblige l'avocat à
   * rouvrir la branche à la main pour voir d'où il part.
   */
  expandPath?: readonly string[];
}

export function DestinationPicker({
  value,
  cases,
  selectedCaseRecord,
  folderTree: rawTree,
  isFolderTreeLoading: isLoading = false,
  onCaseChange,
  onCategoryChange,
  onFolderPathChange,
  categories = MOVABLE_DOCUMENT_CATEGORIES,
  isPathDisabled,
  notice,
  caseLocked = false,
  expandPath,
}: DestinationPickerProps) {
  const [casePickerOpen, setCasePickerOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Libellés adaptés au droit public (« Mes mémoires », « Productions du
  // défendeur ») : ils dépendent du dossier CHOISI, pas du dossier courant.
  const caseItem = selectedCaseRecord ?? null;

  const nodes = useMemo(() => toDestinationTree(rawTree), [rawTree]);

  // Dépliage initial de la branche d'origine. Dépend de l'ARBRE : il arrive
  // après le premier rendu, et changer d'onglet le remplace entièrement.
  const expandKey = expandPath?.join('/') ?? '';
  useEffect(() => {
    if (!expandKey) return;
    const target = expandKey.split('/');
    const ancestors = new Set<string>();
    const walk = (list: DestinationFolderNode[]) => {
      for (const node of list) {
        if (!isWithin(target, node.path)) continue;
        ancestors.add(node.id);
        if (node.children) walk(node.children);
      }
    };
    walk(nodes);
    if (ancestors.size > 0) setExpanded((prev) => new Set([...prev, ...ancestors]));
  }, [nodes, expandKey]);
  // Verrouillé, la liste est vide par construction : le nom vient du dossier lu.
  const selectedCase =
    cases.find((c) => c.id === value.caseId)
    ?? (caseLocked && caseItem ? { id: value.caseId, ...caseItem } : undefined);

  const tabs = useMemo(
    () =>
      // ORDRE CANONIQUE, jamais celui du tableau reçu. Un appelant peut n'offrir
      // qu'un sous-ensemble (une décision ne va pas en « Partie adverse ») et
      // mettre sa destination probable en tête — mais les sections doivent
      // apparaître au MÊME endroit d'un panneau à l'autre, sinon l'avocat
      // reconstruit la carte des onglets à chaque écran. La présélection se dit
      // par la pastille noire, pas par la position.
      MOVABLE_DOCUMENT_CATEGORIES.filter((key) => categories.includes(key)).map((key) => ({
        key,
        label: getCategoryTabLabel(caseItem, key),
        Icon: DOCUMENT_CATEGORIES[key].icon,
      })),
    [categories, caseItem],
  );

  // Sections « Partie adverse i » — même groupement que la grille de l'onglet.
  // Purement visuel : la destination reste le dossier cliqué. Sans lui, un dossier
  // à plusieurs parties listerait 4, 6, 8 dossiers racine à plat sans dire
  // lesquels vont ensemble.
  // Le groupement travaille sur l'arbre BRUT (`FolderNode` : il lit `documentType`,
  // `subcategory` et `sibling_order`, que la normalisation ne conserve pas), puis
  // chaque section est normalisée à son tour en chemins absolus.
  const partieAdverseSections = useMemo(() => {
    if (value.category !== 'partie_adverse' || rawTree.length === 0) return null;
    return groupRootFoldersByPartie([...rawTree]).map((section) => ({
      key: section.key,
      label: section.label,
      nodes: toDestinationTree(section.folders),
    }));
  }, [value.category, rawTree]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const renderNodes = (list: DestinationFolderNode[], level = 0): React.ReactNode =>
    list.map((node) => {
      const isSelected = samePath(value.folderPath, node.path);
      const disabledReason = isPathDisabled?.(node.path, value) ?? null;
      const hasChildren = (node.children?.length ?? 0) > 0;
      const isExpanded = expanded.has(node.id);
      const kind = level === 0 && value.category === 'partie_adverse' ? adverseKindOfRootFolder({ name: node.name, subcategory: node.subcategory }) : undefined;

      return (
        <div key={node.id}>
          <div
            role="option"
            aria-selected={isSelected}
            aria-disabled={disabledReason !== null}
            className={cn(
              'flex items-center gap-2 py-1.5 pr-3 rounded-md transition-colors',
              disabledReason ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
              // Même langage que la grille de dossiers : le fond DIT la
              // sélection, à lui seul. La bordure `accent-blue` d'avant ajoutait
              // 1 px à la ligne choisie, qui sautait donc d'un pixel au clic.
              isSelected && !disabledReason && SELECTION_SURFACE,
              !isSelected && !disabledReason && 'hover:bg-grey-50',
            )}
            style={{ paddingLeft: `${level * 20 + 12}px` }}
            onClick={() => {
              if (!disabledReason) onFolderPathChange(node.path);
            }}
          >
            {hasChildren ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(node.id);
                }}
                aria-label={isExpanded ? `Replier ${node.name}` : `Déplier ${node.name}`}
                className="flex-shrink-0 hover:bg-grey-200 rounded p-0.5"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-grey-600" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-grey-600" />
                )}
              </button>
            ) : (
              <div className="w-5" />
            )}

            <FolderGlyph className="h-5 w-5" />

            <span
              className={cn(
                'text-sm flex-1',
                isSelected && !disabledReason ? 'font-medium text-charcoal' : 'text-charcoal',
                disabledReason && 'text-grey-400',
              )}
            >
              {node.name}
              {disabledReason ? ` (${disabledReason})` : ''}
            </span>

            {/* Le nom d'un dossier partie adverse est librement renommable : seul
                le TAG dit ce qu'il accueille, et c'est lui qui sera posé sur les
                pièces. D'où ce badge, qui ne se déduit jamais du nom. */}
            {kind && (
              <span className="text-[10px] uppercase tracking-wide text-grey-500 border border-grey-200 rounded px-1.5 py-0.5">
                {kindShortLabel(kind)}
              </span>
            )}
          </div>

          {hasChildren && isExpanded && renderNodes(node.children!, level + 1)}
        </div>
      );
    });

  const rootDisabledReason = isPathDisabled?.([], value) ?? null;
  const rootSelectable = rootIsSelectable(value.category);
  const isRootSelected = value.folderPath.length === 0;
  /** L'onglet actif : sa ligne de tête emprunte SON libellé et SON icône. */
  const activeTab = tabs.find((tab) => tab.key === value.category);
  const ActiveTabIcon = activeTab?.Icon;

  return (
    <div className="space-y-4">
      {/* (a) Le dossier */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-grey-500 uppercase tracking-wider">Dossier</label>
        {caseLocked ? (
          <div className="flex items-center rounded-md border border-grey-200 bg-grey-50 px-3 py-2 text-sm text-charcoal">
            <span className="truncate">
              {selectedCase
                ? `${selectedCase.title || 'Dossier sans titre'}${selectedCase.client_name ? ` — ${selectedCase.client_name}` : ''}`
                : 'Dossier courant'}
            </span>
          </div>
        ) : (
        <Popover open={casePickerOpen} onOpenChange={setCasePickerOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={casePickerOpen}
              className="w-full justify-between font-normal"
            >
              <span className={cn('truncate', !selectedCase && 'text-grey-500')}>
                {selectedCase
                  ? `${selectedCase.title || 'Dossier sans titre'}${selectedCase.client_name ? ` — ${selectedCase.client_name}` : ''}`
                  : 'Choisir un dossier'}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          {/* `portal={false}` : ce panneau s'ouvre DANS une Dialog, dont le verrou
              de défilement bloque la molette hors de son sous-arbre. Portalisé, la
              liste s'affichait mais ne défilait qu'à la barre latérale. */}
          <PopoverContent
            className="w-[--radix-popover-trigger-width] p-0"
            align="start"
            portal={false}
          >
            <Command>
              <CommandInput placeholder="Rechercher un dossier..." />
              <CommandList>
                <CommandEmpty>Aucun dossier trouvé.</CommandEmpty>
                <CommandGroup>
                  {cases.map((c) => (
                    <CommandItem
                      key={c.id}
                      // `value` alimente la recherche de `Command` : sans le client,
                      // un dossier ne serait trouvable que par son intitulé.
                      value={`${c.title ?? ''} ${c.client_name ?? ''}`}
                      onSelect={() => {
                        onCaseChange(c.id);
                        setCasePickerOpen(false);
                      }}
                    >
                      <Check
                        className={cn('mr-2 h-4 w-4', c.id === value.caseId ? 'opacity-100' : 'opacity-0')}
                      />
                      <span className="truncate">
                        {c.title || 'Dossier sans titre'}
                        {c.client_name ? ` — ${c.client_name}` : ''}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        )}
      </div>

      {/* (b) L'onglet */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-grey-500 uppercase tracking-wider">Section</label>
        <div className="flex flex-wrap gap-1.5">
          {tabs.map(({ key, label, Icon }) => {
            const isActive = value.category === key;
            return (
              <button
                key={key}
                type="button"
                aria-pressed={isActive}
                onClick={() => onCategoryChange(key)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border',
                  isActive
                    ? 'bg-charcoal text-white border-charcoal'
                    : 'bg-white text-grey-600 border-grey-200 hover:bg-grey-50 hover:border-grey-300',
                )}
              >
                <Icon className="h-3 w-3" />
                {label}
              </button>
            );
          })}
        </div>
        {/* Ni bandeau d'avertissement ici, ni `notice` : la consigne « choisissez
            un dossier Écritures ou Pièces adverses » vivait à DEUX endroits, ce
            bandeau et le pied de la fenêtre, où elle désactive déjà le bouton.
            Retirée ici le 2026-09-15 : une consigne répétée n'est pas deux fois
            plus claire, elle occupe juste la place de ce qu'on doit lire. */}
        {typeof notice === 'function' ? notice(value) : notice}
      </div>

      {/* (c) Le sous-dossier — sans étiquette : l'arbre se désigne lui-même, et
          « Sous-dossier » n'apprenait rien de plus que ce que montre la liste. */}
      <div className="space-y-2">
        {/* Hauteur ÉLASTIQUE : 260 px fixes tenaient sur un grand écran, mais sur
            un portable — et surtout avec un champ de nom au-dessus — ils poussaient
            le reste du panneau hors de la fenêtre. La borne basse garde l'arbre
            utilisable. */}
        <ScrollArea
          className="h-[clamp(140px,28vh,260px)] border border-grey-200 rounded-lg p-2"
          role="listbox"
          aria-label="Sous-dossier"
        >
          {!value.caseId ? (
            <div className="flex items-center justify-center h-32 text-grey-600 text-sm">
              Choisissez d'abord un dossier.
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center h-32 text-grey-600 text-sm">
              Chargement...
            </div>
          ) : (
            <>
              {/* La ligne de tête de l'arbre : ranger DIRECTEMENT dans l'onglet,
                  sans passer par un sous-dossier.
                  Trois états successifs, et pourquoi celui-ci (2026-09-15) :
                  elle a d'abord flotté au-dessus du cadre sous le libellé
                  « Racine de l'onglet » — un bouton posé nulle part, en
                  vocabulaire d'informaticien, qu'on prenait pour un intitulé
                  désactivé. Rangée dans la liste avec un glyphe de dossier, elle
                  faisait passer « Mes écritures » pour un dossier, ce qu'aucune
                  section n'est : elles ne sont ni renommables, ni déplaçables,
                  ni supprimables, et n'existent pas dans l'arborescence.
                  Elle porte donc l'ICÔNE DE SA SECTION, la même que la pastille
                  active juste au-dessus : c'est elle, et elle seule, qui dit que
                  cette ligne n'est pas un dossier. Le libellé reste le nom nu de
                  la section — une phrase explicative (« Directement dans … ») a
                  été essayée puis retirée le même jour : l'icône suffisant à
                  faire la différence, elle n'ajoutait que du bruit sur la ligne.
                  Les dossiers en dessous sont ses enfants, d'où leur décalage
                  d'un niveau. */}
              {rootSelectable && (
                <div
                  role="option"
                  aria-selected={isRootSelected}
                  aria-disabled={rootDisabledReason !== null}
                  className={cn(
                    'flex items-center gap-2 py-1.5 pl-3 pr-3 rounded-md transition-colors',
                    rootDisabledReason ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                    isRootSelected && !rootDisabledReason && SELECTION_SURFACE,
                    !isRootSelected && !rootDisabledReason && 'hover:bg-grey-50',
                  )}
                  onClick={() => {
                    if (!rootDisabledReason) onFolderPathChange([]);
                  }}
                >
                  <div className="w-5" />
                  <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center">
                    {ActiveTabIcon ? <ActiveTabIcon className="h-4 w-4 text-grey-500" /> : null}
                  </span>
                  <span
                    className={cn(
                      'text-sm flex-1 text-charcoal',
                      isRootSelected && !rootDisabledReason && 'font-medium',
                      rootDisabledReason && 'text-grey-400',
                    )}
                  >
                    {activeTab?.label}
                    {rootDisabledReason ? ` (${rootDisabledReason})` : ''}
                  </span>
                </div>
              )}

              {nodes.length === 0 ? (
                <div
                  className={cn(
                    'text-grey-600 text-sm',
                    // Sans ligne de racine au-dessus, le cadre est vide : on
                    // centre. Avec elle, un message centré flotterait sous une
                    // ligne rangée à gauche.
                    rootSelectable ? 'px-3 py-3' : 'flex items-center justify-center h-32',
                  )}
                >
                  Aucun sous-dossier dans cet onglet.
                </div>
              ) : partieAdverseSections ? (
                partieAdverseSections.map((section) => (
                  <div key={section.key} className="mb-3 last:mb-0">
                    <h4 className="text-xs font-medium text-grey-500 uppercase tracking-wide px-3 py-1">
                      {section.label}
                    </h4>
                    {renderNodes(section.nodes)}
                  </div>
                ))
              ) : (
                renderNodes(nodes, rootSelectable ? 1 : 0)
              )}
            </>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}
